import React, { ReactNode } from 'react'
import { css, cx } from '@emotion/css'

export function Row({
  children,
  className
}: {
  readonly children: ReactNode
  readonly className?: string
}) {
  return (
    <div
      className={cx(
        css`
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;

          margin-left: -30px;
        `,
        className
      )}
    >
      {children}
    </div>
  )
}

type Breakpoint = 'sm' | 'md' | 'lg'

function getBreakPoint(breakpoint: Breakpoint): string {
  switch (breakpoint) {
    case 'sm':
      return '480'
    case 'md':
      return '768'
    case 'lg':
      return '1024'
  }
}

export type Basis = 12 | 10 | 9 | 8 | 7 | 6 | 5 | 4 | 3 | 2 | 'content'

function getBasis(basis: Basis): string {
  switch (basis) {
    case 'content':
      return 'auto'
    case 2:
      return 'calc(16% - 30px)'
    case 3:
      return 'calc(25% - 30px)'
    case 4:
      return 'calc(33% - 30px)'
    case 5:
      return 'calc(41% - 30px)'
    case 6:
      return 'calc(50% - 30px)'
    case 7:
      return 'calc(58% - 30px)'
    case 8:
      return 'calc(66% - 30px)'
    case 9:
      return 'calc(75% - 30px)'
    case 10:
      return 'calc(83% - 30px)'
    case 12:
      return 'calc(100% - 30px)'
  }
}

export function Box({
  children,
  basis = 12,
  breakpoint = 'md',
  grow,
  hidden,
  className
}: {
  /**
   * Sets the initial width of the box expressed as a fraction of 12
   * (e.g. 12 is full width, 6 is half width, etc), or "content"
   * which sets it to the width of the Box's content.
   */
  readonly basis?: Basis

  readonly children: ReactNode

  /**
   * Defines the viewport width at which the box will become full width.
   * Ignored if basis is set to content and grow is false.
   */
  readonly breakpoint?: Breakpoint

  /**
   * If false, prevents the Box from growing beyond it's basis. It is true
   * by default.
   */
  readonly grow?: boolean

  /**
   * Hide element from both users and user agents
   */
  readonly hidden?: boolean

  readonly className?: string
}) {
  return (
    <div
      aria-hidden={hidden}
      hidden={hidden}
      className={cx(
        'legacy-ds-box',
        css`
          display: ${hidden ? 'none !important' : 'block'};
          flex-grow: ${grow ? 1 : 0};
          flex-shrink: 0;
          flex-basis: ${getBasis(basis)};
          padding: 8px 0;

          margin-left: 30px;

          @media (max-width: ${getBreakPoint(breakpoint)}px) {
            flex-basis: ${basis === 'content' && !grow
              ? 'auto'
              : 'calc(100% - 30px)'};
          }
        `,
        className
      )}
    >
      {children}
    </div>
  )
}
